// Turns an AxiosError into the stable, sanitized description a Sentry issue is
// named and fingerprinted by.
//
// Applies to every axios call in the app: the generated
// `@spazious/client-backoffice` instance and the bare `auth/refresh/` request the
// token refresh helper issues outside of it. The description is derived from the
// error object, so both are covered without instrumenting either instance.

import axios, { type AxiosError } from 'axios';

import { canonicalizeRoute } from './canonicalRoute.js';
import {
  MAX_API_ERRORS,
  MAX_CODE_LENGTH,
  MAX_DETAIL_LENGTH,
  MAX_POINTER_LENGTH,
  sanitizeText,
  truncate,
} from './sanitize.js';

// A JSON:API error is reduced to three fields on its way out of the browser:
// `code`, `source.pointer` and `detail`. `source.parameter` is deliberately
// dropped — it names a query parameter and its value tends to be echoed with it.
export interface ApiErrorDescription {
  // Uppercased HTTP verb, or `UNKNOWN` when axios failed before building a request.
  method: string;
  // Canonical path with record identifiers collapsed (see `canonicalizeRoute`).
  route: string;
  // HTTP status, or the axios transport code when the request never got a
  // response (`ERR_NETWORK`, `ECONNABORTED`, ...).
  outcome: string;
  // Sorted, deduplicated backend error codes — the only part of the response body
  // stable enough to publish as a searchable facet. Not part of grouping identity:
  // see `applyApiErrorDescription`.
  codes: string[];
  // One line per backend error, `code | pointer | detail`. Flat on purpose: Sentry
  // normalizes `contexts` to a depth of 3 by default, which would render an array
  // of objects nested this deep as `"[Object]"` and lose the whole payload. A
  // string survives at any depth, so the errors stay readable without depending on
  // a global SDK setting.
  errors: string[];
  // Whether the backend answered a deliberate, user-recoverable outcome rather
  // than failing. Demotes the event to `warning` and skips its session replay:
  // see `applyApiErrorDescription`.
  expected: boolean;
}

const UNKNOWN_METHOD = 'UNKNOWN';
const UNKNOWN_OUTCOME = 'ERR_UNKNOWN';

// Requests aborted on purpose (React Query cancelling a superseded query, a
// component unmounting mid-flight). Never a defect.
const isCanceledRequest = (error: AxiosError): boolean =>
  axios.isCancel(error) || error.code === 'ERR_CANCELED';

// A transport failure while the browser reports no connectivity is the network,
// not the app. `navigator.onLine === true` is unreliable in the other direction
// (it only proves a link exists), so only a *confirmed* offline state suppresses.
const isConfirmedOffline = (error: AxiosError): boolean =>
  error.code === 'ERR_NETWORK' && typeof navigator !== 'undefined' && navigator.onLine === false;

// The 401 that ends a session after its refresh was rejected is the expected
// outcome of an expired session, not an incident: the refresh helper already
// reports *why* the refresh failed via an explicit warning. Marked by the 401
// retry interceptor on the original error. A WeakSet keeps the axios error object
// untouched (and un-leaked) instead of branding it with a flag that would travel
// into every consumer.
const expectedAuthLogoutErrors = new WeakSet<object>();

/** Mark `error` as the expected 401 that completes a logout after a failed refresh. */
export const markExpectedAuthLogout = (error: unknown): void => {
  if (typeof error === 'object' && error !== null) {
    expectedAuthLogoutErrors.add(error);
  }
};

const isExpectedAuthLogout = (error: AxiosError): boolean => expectedAuthLogoutErrors.has(error);

/** `error` if it is an axios error, otherwise `null`. */
export const asAxiosError = (error: unknown): AxiosError | null =>
  axios.isAxiosError(error) ? error : null;

/** Whether this API failure is expected noise that must not reach Sentry. */
export const isSuppressedApiError = (error: AxiosError): boolean =>
  isCanceledRequest(error) || isConfirmedOffline(error) || isExpectedAuthLogout(error);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

// One backend error rendered as `code | pointer | detail`, each field scrubbed and
// truncated to its own limit. Returns `null` when nothing survives the allow-list.
const toSanitizedApiError = (raw: unknown): string | null => {
  if (!isRecord(raw)) {
    return null;
  }

  const code = readString(raw.code);
  const pointer = isRecord(raw.source) ? readString(raw.source.pointer) : undefined;
  const detail = readString(raw.detail);

  const parts = [
    code && truncate(code, MAX_CODE_LENGTH),
    pointer && truncate(pointer, MAX_POINTER_LENGTH),
    detail && truncate(sanitizeText(detail), MAX_DETAIL_LENGTH),
  ].filter((part): part is string => typeof part === 'string');

  return parts.length ? parts.join(' | ') : null;
};

// JSON:API responses put failures under a top-level `errors` array. The body is an
// untyped boundary, so every field is read defensively.
const extractRawErrors = (error: AxiosError): unknown[] => {
  const { data } = error.response ?? {};
  const rawErrors = isRecord(data) ? data.errors : undefined;

  return Array.isArray(rawErrors) ? rawErrors : [];
};

// Deduplicated and sorted over the *whole* error list, so the `api.code` tag reads
// the same when the backend returns the same set of errors in a different order.
const extractCodes = (rawErrors: unknown[]): string[] =>
  [
    ...new Set(
      rawErrors
        .map((raw) => (isRecord(raw) ? readString(raw.code) : undefined))
        .filter((code): code is string => code !== undefined)
    ),
  ].sort();

// The tag value is capped only after the full list has been read, so a code past
// the cap can never be hidden from `isExpectedOutcome`.
const capCodes = (codes: string[]): string[] =>
  codes.slice(0, MAX_API_ERRORS).map((code) => truncate(code, MAX_CODE_LENGTH));

/**
 * Backend error codes the operator can act on: a rejected form field, or a
 * business rule the flow states up front. An allow-list on purpose — a code that
 * is not named here leaves its failure an incident.
 */
export const EXPECTED_BACKEND_CODES: ReadonlySet<string> = new Set([
  'account_exists',
  'attendees_required',
  'blank',
  'invalid_choice',
  'order_contact_required',
  'order_lead_already_confirmed',
  'order_requests_at_greater_than_today',
  'payment_order_is_draft',
  'required',
  'space_unavailable',
  'start_date_required',
  'unique',
]);

// A missing record and a rate limit answer in the same shape as a rejected form,
// and neither is something the operator can correct.
const NEVER_EXPECTED_STATUSES = new Set([404, 429]);

// Every code has to be recognised: one authorization or unknown code in the same
// response makes the whole failure an incident again.
const isExpectedOutcome = (status: number | undefined, codes: string[]): boolean =>
  status !== undefined &&
  status >= 400 &&
  status < 500 &&
  !NEVER_EXPECTED_STATUSES.has(status) &&
  codes.length > 0 &&
  codes.every((code) => EXPECTED_BACKEND_CODES.has(code));

/**
 * Stable description of an API failure: what was called, where, how it ended,
 * which backend codes came back and whether the backend meant to answer that.
 * Everything here is safe to send and stable across callers, which is what makes
 * it usable as a fingerprint.
 */
export const describeApiError = (error: AxiosError): ApiErrorDescription => {
  const { config, response } = error;
  const rawErrors = extractRawErrors(error);
  const codes = extractCodes(rawErrors);

  return {
    method: config?.method?.toUpperCase() ?? UNKNOWN_METHOD,
    route: canonicalizeRoute(config?.url ?? '', config?.baseURL),
    outcome: response ? String(response.status) : error.code ?? UNKNOWN_OUTCOME,
    codes: capCodes(codes),
    errors: rawErrors
      .map(toSanitizedApiError)
      .filter((apiError): apiError is string => apiError !== null)
      .slice(0, MAX_API_ERRORS),
    expected: isExpectedOutcome(response?.status, codes),
  };
};
